import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { API_BASE } from "@/lib/api";
import { volunteerApi, getVolunteerToken, clearVolunteerSession, getVolunteerName, humanDay, humanTime, EVENT_DAY_FRIDAY, EVENT_DAY_SATURDAY } from "@/lib/eventHub";
import { Button } from "@/components/ui/button";
import { Search, Users, CheckCircle2, Undo2, LogOut, RefreshCw, Loader2, AlertCircle } from "lucide-react";

const CATEGORIES = [
  "Guest", "Honoree", "Sponsor", "Speaker", "Panelist", "Trailblazer",
  "Volunteer", "Board Member", "Vendor", "Staff", "Media", "Other",
];

const REFRESH_INTERVAL_MS = 30_000;

export default function VolunteerDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ event_day: "", walk_in: null, category: "" });
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [notice, setNotice] = useState(null);
  const [connected, setConnected] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const noticeTimer = useRef(null);
  const sseRef = useRef(null);

  const showNotice = useCallback((n) => {
    setNotice(n);
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), 8000);
  }, []);

  const loadAll = useCallback(async () => {
    setRefreshing(true);
    try {
      const [ciRes, sRes] = await Promise.all([
        volunteerApi().get("/volunteer/checkins", { params: {
          event_day: filters.event_day || undefined,
          walk_in: filters.walk_in === null ? undefined : filters.walk_in,
          category: filters.category || undefined,
          limit: 300,
        }}),
        volunteerApi().get("/volunteer/stats"),
      ]);
      setCheckins(ciRes.data.check_ins || []);
      setStats(sRes.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearVolunteerSession();
        navigate("/event/volunteer-login", { replace: true });
      }
    } finally {
      setRefreshing(false);
    }
  }, [filters, navigate]);

  // Initial load + auth check
  useEffect(() => {
    (async () => {
      try {
        const { data } = await volunteerApi().get("/volunteer/me");
        setMe(data);
      } catch {
        clearVolunteerSession();
        navigate("/event/volunteer-login", { replace: true });
        return;
      }
      loadAll();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh on filter change
  useEffect(() => { loadAll(); }, [filters]); // eslint-disable-line

  // Poll every 30s as backstop
  useEffect(() => {
    const t = setInterval(loadAll, REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [loadAll]);

  // SSE — live check-in stream
  useEffect(() => {
    const token = getVolunteerToken() || localStorage.getItem("bs_session_token") || "";
    if (!token) return;
    const url = `${API_BASE}/event/stream/checkins?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url, { withCredentials: false });
    sseRef.current = es;
    es.addEventListener("hello", () => setConnected(true));
    es.addEventListener("checkin", (ev) => {
      try {
        const data = JSON.parse(ev.data);
        const ci = data.check_in;
        const att = data.attendee;
        // Prepend to list
        setCheckins((cur) => {
          if (cur.some((c) => c.id === ci.id)) return cur;
          return [{
            id: ci.id,
            attendee_id: att.id,
            attendee_name: `${att.first_name} ${att.last_name}`.trim(),
            attendee_email: att.email,
            attendee_phone: att.phone,
            attendee_organization: att.organization,
            attendee_categories: att.categories || [],
            attendee_walk_in: att.walk_in,
            attendee_attending_as: att.attending_as || (att.walk_in ? "walkin" : "individual"),
            attendee_host_name: att.host_name || "",
            attendee_host_id: att.host_id || null,
            event_day: ci.event_day,
            status: ci.status,
            method: ci.method,
            walk_in: ci.walk_in,
            checked_in_at: ci.checked_in_at,
            checked_in_by: ci.checked_in_by,
            notes: ci.notes || [],
          }, ...cur];
        });
        // Refresh stats
        volunteerApi().get("/volunteer/stats").then((r) => setStats(r.data)).catch(() => {});
        showNotice({
          name: `${att.first_name} ${att.last_name}`.trim(),
          category: (att.categories && att.categories[0]) || "Guest",
          time: ci.checked_in_at,
          host_name: att.host_name || "",
          attending_as: att.attending_as || "individual",
        });
      } catch { /* noop */ }
    });
    es.addEventListener("checkin_removed", (ev) => {
      try {
        const data = JSON.parse(ev.data);
        setCheckins((cur) => cur.filter((c) => c.id !== data.check_in_id));
      } catch { /* noop */ }
    });
    es.onerror = () => setConnected(false);
    return () => { es.close(); sseRef.current = null; };
  }, [showNotice]);

  const doSearch = async (v) => {
    setSearch(v);
    if (!v || v.length < 2) { setSearchResults([]); return; }
    try {
      const { data } = await volunteerApi().get("/volunteer/attendees/search", { params: { q: v } });
      setSearchResults(data.attendees || []);
    } catch { /* noop */ }
  };

  const doManualCheckIn = async (attendee, event_day) => {
    try {
      await volunteerApi().post("/volunteer/checkin/manual", { attendee_id: attendee.id, event_day });
      toast.success(`Checked in ${attendee.first_name} ${attendee.last_name}`);
      setSearchResults([]);
      setSearch("");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Manual check-in failed");
    }
  };

  const undoCheckIn = async (id) => {
    if (!window.confirm("Undo this check-in? This cannot be undone quickly.")) return;
    try {
      await volunteerApi().post(`/volunteer/checkin/${id}/undo`);
      toast.success("Check-in undone");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Undo failed");
    }
  };

  const logout = async () => {
    try { await volunteerApi().post("/volunteer/logout"); } catch { /* noop */ }
    clearVolunteerSession();
    navigate("/event/volunteer-login", { replace: true });
  };

  const filteredCheckins = useMemo(() => checkins, [checkins]);

  return (
    <div className="min-h-screen bg-emerald-50/40 pb-6">
      <header className="sticky top-0 z-20 bg-white border-b border-emerald-950/10">
        <div className="mx-auto max-w-5xl px-4 py-3 flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-[Outfit] font-bold text-fuchsia-700">Volunteer Dashboard</div>
            <div className="font-[Cormorant_Garamond] text-xl leading-tight text-emerald-950">
              {me?.name || getVolunteerName() || "Volunteer"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`hidden sm:inline-flex items-center gap-1 text-[10px] font-[Outfit] font-bold uppercase tracking-widest px-2 py-1 rounded-full ${
              connected ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
            }`}>
              <span className={`h-1.5 w-1.5 rounded-full ${connected ? "bg-emerald-600 animate-pulse" : "bg-amber-600"}`} />
              {connected ? "Live" : "Reconnecting"}
            </span>
            <button
              data-testid="volunteer-refresh"
              onClick={loadAll}
              className="rounded-full p-2 hover:bg-emerald-100"
              aria-label="Refresh"
            >
              <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
            </button>
            <button
              data-testid="volunteer-logout"
              onClick={logout}
              className="rounded-full p-2 hover:bg-emerald-100"
              aria-label="Log out"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      {/* Live notification toast (transient) */}
      {notice && (
        <div
          data-testid="volunteer-live-toast"
          className="mx-auto max-w-5xl px-4 mt-3"
        >
          <div className="rounded-2xl bg-fuchsia-600 text-white px-4 py-3 shadow-lg flex items-center gap-3 animate-in slide-in-from-top duration-300">
            <CheckCircle2 size={20} />
            <div className="flex-1 min-w-0">
              <div className="font-[Cormorant_Garamond] text-lg leading-tight truncate">{notice.name} — Checked In</div>
              <div className="text-[11px] font-[Outfit] font-medium text-fuchsia-100">
                {notice.attending_as === "group" && notice.host_name
                  ? <>Guest of {notice.host_name} · </>
                  : <>{notice.category} · </>}
                {new Date(notice.time).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-5xl px-4 mt-4 space-y-4">
        {/* Stats */}
        <section data-testid="volunteer-stats" className="grid grid-cols-2 sm:grid-cols-6 gap-2">
          <StatCard label="Total Checked In" value={stats?.total_checked_in ?? "—"} />
          <StatCard label="Friday" value={stats?.friday_checked_in ?? "—"} accent="fuchsia" />
          <StatCard label="Saturday" value={stats?.saturday_checked_in ?? "—"} accent="fuchsia" />
          <StatCard label="Registered" value={stats?.total_registered ?? "—"} />
          <StatCard label="Walk-Ins" value={stats?.walk_ins ?? "—"} />
          <StatCard label="Percent Checked" value={stats?.percentage_checked_in != null ? `${stats.percentage_checked_in}%` : "—"} />
        </section>

        {/* Search + manual check-in */}
        <section data-testid="volunteer-search-section" className="rounded-2xl bg-white p-4 border border-emerald-950/10">
          <div className="flex items-center gap-2 mb-2">
            <Users size={16} className="text-emerald-950/60" />
            <h2 className="font-[Cormorant_Garamond] text-lg text-emerald-950">Find & Check In a Guest</h2>
          </div>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-950/40" />
            <input
              data-testid="volunteer-search-input"
              type="text"
              placeholder="Search by name, email, phone, or organization…"
              value={search}
              onChange={(e) => doSearch(e.target.value)}
              className="w-full h-11 rounded-xl border-2 border-emerald-950/10 bg-white pl-9 pr-3 font-[Outfit] text-sm focus:border-fuchsia-500 outline-none"
            />
          </div>
          {searchResults.length > 0 && (
            <div className="mt-3 space-y-2">
              {searchResults.map((a) => (
                <div
                  key={a.id}
                  data-testid={`volunteer-search-result-${a.id}`}
                  className="rounded-xl border border-emerald-950/10 p-3 flex items-center justify-between gap-3 bg-emerald-50/50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-[Outfit] font-bold text-sm text-emerald-950 truncate">
                      {a.first_name} {a.last_name}
                      {a.walk_in && <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded-full bg-fuchsia-100 text-fuchsia-700 font-bold uppercase tracking-wider">walk-in</span>}
                    </div>
                    <div className="text-[11px] text-emerald-950/60 truncate">{a.email}{a.phone && ` · ${a.phone}`}</div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      data-testid={`volunteer-checkin-fri-${a.id}`}
                      onClick={() => doManualCheckIn(a, EVENT_DAY_FRIDAY)}
                      className="rounded-lg h-8 px-2.5 text-[11px] bg-emerald-950 hover:bg-emerald-900 font-[Outfit] font-semibold"
                    >Fri</Button>
                    <Button
                      data-testid={`volunteer-checkin-sat-${a.id}`}
                      onClick={() => doManualCheckIn(a, EVENT_DAY_SATURDAY)}
                      className="rounded-lg h-8 px-2.5 text-[11px] bg-fuchsia-600 hover:bg-fuchsia-700 font-[Outfit] font-semibold"
                    >Sat</Button>
                    <Button
                      data-testid={`volunteer-checkin-both-${a.id}`}
                      onClick={() => doManualCheckIn(a, "both")}
                      variant="outline"
                      className="rounded-lg h-8 px-2.5 text-[11px] border-emerald-950 text-emerald-950 font-[Outfit] font-semibold"
                    >Both</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {search.length >= 2 && searchResults.length === 0 && (
            <div
              data-testid="volunteer-no-results"
              className="mt-3 rounded-xl border border-dashed border-fuchsia-300 bg-fuchsia-50 p-3 text-sm font-[Outfit] text-emerald-950"
            >
              <div className="flex items-start gap-2">
                <AlertCircle size={16} className="text-fuchsia-700 mt-0.5" />
                <div>
                  No match for &ldquo;{search}&rdquo;.{" "}
                  <Link
                    to="/event/checkin"
                    data-testid="volunteer-add-walkin-link"
                    className="font-bold text-fuchsia-700 underline"
                  >
                    Add walk-in from public check-in page →
                  </Link>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Filters */}
        <section data-testid="volunteer-filters" className="flex flex-wrap gap-2">
          {[
            { l: "All days", v: "" },
            { l: "Friday", v: EVENT_DAY_FRIDAY },
            { l: "Saturday", v: EVENT_DAY_SATURDAY },
          ].map((opt) => (
            <button
              key={opt.v}
              data-testid={`filter-day-${opt.v || "all"}`}
              onClick={() => setFilters({ ...filters, event_day: opt.v })}
              className={`px-3 h-8 rounded-full text-xs font-[Outfit] font-semibold transition-all ${
                filters.event_day === opt.v ? "bg-emerald-950 text-white" : "bg-white border border-emerald-950/15 text-emerald-950"
              }`}
            >
              {opt.l}
            </button>
          ))}
          <button
            data-testid="filter-walkins"
            onClick={() => setFilters({ ...filters, walk_in: filters.walk_in === true ? null : true })}
            className={`px-3 h-8 rounded-full text-xs font-[Outfit] font-semibold transition-all ${
              filters.walk_in === true ? "bg-fuchsia-600 text-white" : "bg-white border border-emerald-950/15 text-emerald-950"
            }`}
          >
            Walk-ins only
          </button>
          <select
            data-testid="filter-category"
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            className="h-8 rounded-full text-xs font-[Outfit] font-semibold bg-white border border-emerald-950/15 text-emerald-950 px-3"
          >
            <option value="">All categories</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </section>

        {/* Recent check-ins */}
        <section data-testid="volunteer-checkins-list" className="rounded-2xl bg-white border border-emerald-950/10 overflow-hidden">
          <div className="px-4 py-3 border-b border-emerald-950/10 flex items-center justify-between">
            <h2 className="font-[Cormorant_Garamond] text-lg text-emerald-950">Recent Arrivals</h2>
            <span className="text-[10px] font-[Outfit] font-semibold uppercase tracking-widest text-emerald-950/50">
              {filteredCheckins.length} shown
            </span>
          </div>
          {filteredCheckins.length === 0 ? (
            <div className="p-8 text-center text-sm font-[Outfit] text-emerald-950/50">
              {refreshing ? <Loader2 className="mx-auto animate-spin" /> : "No check-ins yet. Guests will appear here in real time."}
            </div>
          ) : (
            <ul className="divide-y divide-emerald-950/5">
              {filteredCheckins.map((c) => (
                <li key={c.id} data-testid={`checkin-row-${c.id}`} className="px-4 py-3 flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center font-[Outfit] font-bold text-sm shrink-0">
                    {(c.attendee_name || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-[Outfit] font-bold text-sm text-emerald-950 truncate">
                      {c.attendee_name}
                      {c.walk_in && <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded-full bg-fuchsia-100 text-fuchsia-700 font-bold uppercase tracking-wider">walk-in</span>}
                      {c.attendee_attending_as === "group" && c.attendee_host_name && (
                        <span
                          data-testid={`checkin-guest-of-${c.id}`}
                          className="ml-2 text-[9px] px-1.5 py-0.5 rounded-full bg-fuchsia-600 text-white font-bold uppercase tracking-wider"
                        >
                          Guest of {c.attendee_host_name}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] font-[Outfit] text-emerald-950/60 truncate">
                      {c.attendee_categories?.[0] || "Guest"} · {humanDay(c.event_day)} ·{" "}
                      {new Date(c.checked_in_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                      {c.attendee_organization && ` · ${c.attendee_organization}`}
                    </div>
                    {(c.attendee_email || c.attendee_phone) && (
                      <div className="text-[10px] font-[Outfit] text-emerald-950/50 truncate mt-0.5">
                        {c.attendee_email}
                        {c.attendee_email && c.attendee_phone && " · "}
                        {c.attendee_phone}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span data-testid={`checkin-status-${c.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[10px] font-[Outfit] font-bold uppercase tracking-wider">
                      <CheckCircle2 size={10} /> In
                    </span>
                    <button
                      data-testid={`undo-${c.id}`}
                      onClick={() => undoCheckIn(c.id)}
                      className="rounded-full p-1.5 text-emerald-950/40 hover:text-fuchsia-700 hover:bg-fuchsia-50"
                      title="Undo check-in"
                    >
                      <Undo2 size={14} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  const border = accent === "fuchsia" ? "border-fuchsia-200 bg-fuchsia-50/50" : "border-emerald-950/10 bg-white";
  return (
    <div className={`rounded-xl border-2 ${border} p-3`}>
      <div className="text-[10px] font-[Outfit] font-bold uppercase tracking-widest text-emerald-950/50">{label}</div>
      <div className="mt-1 font-[Cormorant_Garamond] text-2xl leading-tight text-emerald-950">{value}</div>
    </div>
  );
}
